import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type DO_NOT_USE_OR_YOU_WILL_BE_FIRED_CALLBACK_REF_RETURN_VALUES,
	type ScriptHTMLAttributes,
} from 'react';

import {
	type FileIdentifier,
	type FileState,
	isImageRepresentationReady,
	type MediaBlobUrlAttrs,
	type MediaStoreGetFileImageParams,
	toCommonMediaClientError,
} from '@atlaskit/media-client';
import { isCDNEnabled } from '@atlaskit/media-client/media-cdn';
import { useCopyIntent } from '@atlaskit/media-client-react/use-copy-intent';
import { useFileState } from '@atlaskit/media-client-react/use-file-state';
import { useMediaClient } from '@atlaskit/media-client-react/use-media-client';
import {
	isMimeTypeSupportedByBrowser,
	type MediaTraceContext,
	type SSR,
} from '@atlaskit/media-common';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useInteractionContext } from '@atlaskit/react-ufo/use-interaction-context';

import { ImageLoadError } from './ImageLoadError';
import { MediaFilePreviewError } from './MediaFilePreviewError';
import type { SSRStatus } from './analytics';
import { createFailedSSRObject } from './createFailedSSRObject';
import { createRequestDimensions } from './createRequestDimensions';
import { ensureMediaFilePreviewError } from './ensureMediaFilePreviewError';
import { extractErrorInfo } from './extractErrorInfo';
import { mediaFilePreviewCache } from './getPreview/cache';
import { getAndCacheLocalPreview } from './getPreview/getAndCacheLocalPreview';
import { getAndCacheRemotePreview } from './getPreview/getAndCacheRemotePreview';
import { getSSRPreview } from './getPreview/getSSRPreview';
import { isLocalPreview } from './getPreview/isLocalPreview';
import { isRemotePreview } from './getPreview/isRemotePreview';
import { isSSRClientPreview } from './getPreview/isSSRClientPreview';
import { isSSRDataPreview } from './getPreview/isSSRDataPreview';
import { isSSRPreview } from './getPreview/isSSRPreview';
import { isSupportedLocalPreview } from './getPreview/isSupportedLocalPreview';
import { generateScriptProps } from './globalScope/generateScriptProps';
import { getSSRData } from './globalScope/getSSRData';
import { isBigger } from './isBigger';
import { isWider } from './isWider';
import {
	type MediaFilePreview,
	type MediaFilePreviewDimensions,
	type MediaFilePreviewStatus,
} from './types';
import { useCurrentValueRef } from './useCurrentValueRef';

// invisible gif for SSR preview to show the underlying spinner until the src is replaced by
// the actual image src in the inline script
const DEFAULT_SSR_PREVIEW: MediaFilePreview = {
	dataURI: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
	dimensions: { width: 100, height: 100 },
	source: 'ssr-server',
};

export interface UseFilePreviewParams {
	/** Instance of file identifier. */
	readonly identifier: FileIdentifier;
	/** Resize the media to 'crop' | 'fit' | 'full-fit' */
	readonly resizeMode?: MediaStoreGetFileImageParams['mode'];
	/** Dimensions to be requested to the server. Will be scaled x2 in Retina Displays */
	readonly dimensions?: MediaFilePreviewDimensions;
	/** Server-Side-Rendering modes are "server" and "client" */
	readonly ssr?: SSR;
	/** Whether to use the srcSet for the preview. */
	readonly useSrcSet?: boolean; // Defaults to false
	/** Attributes to attach to the created Blob Url */
	readonly mediaBlobUrlAttrs?: MediaBlobUrlAttrs; // FOR COPY & PASTE
	/** Trace context to be passed to the backend requests */
	readonly traceContext?: MediaTraceContext;
	/** Do not fetch a remote preview. Helpful for lazy loading */
	readonly skipRemote?: boolean;
	/** Define whether an animated image is acceptable to return */
	readonly allowAnimated?: boolean;
	/** Define the upscale strategy for this image. */
	readonly upscale?: boolean;
	/** Make the client receive the response with the given max-age cache control header. Minimum: 0, maximum: 9223372036854776000.
	 */
	readonly maxAge?: number;
	/** Defines the source component */
	readonly source?: string;
	/** Initial file state to be used for the preview. */
	readonly initialFileState?: FileState;
}

export const useFilePreview = ({
	resizeMode = 'crop',
	identifier,
	ssr,
	dimensions,
	useSrcSet = false,
	traceContext,
	skipRemote,
	mediaBlobUrlAttrs, // TODO: mediaBlobUrlAttrs can be missing several values contained in the file details. The preview hook is not updating the params in the preview after the file details are available.
	allowAnimated = true,
	upscale,
	maxAge,
	source,
	initialFileState,
}: UseFilePreviewParams): {
	preview: MediaFilePreview | undefined;
	status: MediaFilePreviewStatus;
	error: MediaFilePreviewError | undefined;
	nonCriticalError: MediaFilePreviewError | undefined;
	ssrReliability: SSRStatus;
	onImageError: (failedPreview?: MediaFilePreview) => void;
	onImageLoad: (newPreview?: MediaFilePreview) => void;
	getSsrScriptProps: (() => ScriptHTMLAttributes<HTMLScriptElement>) | undefined;
	copyNodeRef: (
		instance: HTMLDivElement | HTMLImageElement | null,
	) =>
		| void
		| DO_NOT_USE_OR_YOU_WILL_BE_FIRED_CALLBACK_REF_RETURN_VALUES[keyof DO_NOT_USE_OR_YOU_WILL_BE_FIRED_CALLBACK_REF_RETURN_VALUES];
} => {
	const mediaClient = useMediaClient();
	const [status, setStatus] = useState<MediaFilePreviewStatus>('loading');

	const [error, setError] = useState<MediaFilePreviewError | undefined>();
	const [nonCriticalError, setNonCriticalError] = useState<MediaFilePreviewError | undefined>();
	const [isBannedLocalPreview, setIsBannedLocalPreview] = useState(false);
	const [upfrontPreviewStatus, setUpfrontPreviewStatus] = useState<
		'not-resolved' | 'resolving' | 'resolved'
	>('not-resolved');
	const ssrReliabilityRef = useRef<SSRStatus>({
		server: { status: 'unknown' },
		client: { status: 'unknown' },
	});
	const ufoContext = useInteractionContext();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useLayoutEffect(() => {
		if (isLoading) {
			return ufoContext?.hold('img-loading');
		}
	}, [ufoContext, isLoading]);

	const requestDimensions = useMemo(
		() => (dimensions ? createRequestDimensions(dimensions) : undefined),
		[dimensions],
	);
	const requestDimensionsRef = useCurrentValueRef(requestDimensions);

	let imageURLParams: MediaStoreGetFileImageParams = {
		collection: identifier.collectionName,
		mode: resizeMode,
		...requestDimensions,
		allowAnimated,
		upscale,
		'max-age': maxAge,
	};

	imageURLParams = { ...imageURLParams, source };

	const previewInitializer = (): MediaFilePreview | undefined => {
		const preview = mediaFilePreviewCache.get(identifier.id, resizeMode);
		if (preview) {
			return preview;
		}

		if (ssr) {
			const ssrData = getSSRData(identifier, resizeMode);
			if (ssrData?.error) {
				ssrReliabilityRef.current = {
					...ssrReliabilityRef.current,
					server: {
						status: 'fail',
						...ssrData.error,
					},
				};
			}

			if (!ssrData?.dataURI) {
				// Only attempt SSR preview generation if:
				// 1. We're on the server (ssr='server'), OR
				// 2. We're on the client AND there is SSR data (meaning SSR actually happened)
				// If ssr='client' but there's no SSR data, it means this is a client-side navigation
				// where no SSR occurred, so we should skip SSR preview generation entirely.
				if (ssr === 'server' || ssrData) {
					try {
						// When the relay/SSR-seed path provided a full pre-signed CDN asset
						// URL, pass it through to getImageUrlSync as the base. That inserts
						// the client's image-transform params before &wm-ari and preserves
						// the backend's v2 CDN path, CloudFront signature and watermark
						// anchors that a rebuilt /file/{id}/image/cdn URL cannot reproduce.
						const seededCdnUrl =
							initialFileState && initialFileState.status !== 'error'
								? initialFileState.previewCdnUrl
								: undefined;
						// Only use the seeded CDN URL when CDN delivery is actually in use.
						// isCDNEnabled() is false for path-based routing (and for isolated
						// cloud / GCP), where the signed media-cdn URL is not served — there
						// getImageUrlSync must build the URL on the product's own /media-api
						// host instead.
						const ssrSeededCdnUrl =
							seededCdnUrl && isCDNEnabled() && fg('platform_media_ssr_data_seed')
								? seededCdnUrl
								: undefined;
						return getSSRPreview(
							ssr,
							mediaClient,
							identifier.id,
							imageURLParams,
							mediaBlobUrlAttrs,
							ssrSeededCdnUrl,
						);
					} catch (e: any) {
						ssrReliabilityRef.current = {
							...ssrReliabilityRef.current,
							[ssr]: {
								status: 'fail',
								...extractErrorInfo(e, traceContext),
							},
						};
					}
				}
			} else {
				const { dimensions, dataURI, srcSet, loading } = ssrData;
				return { dataURI, dimensions, source: 'ssr-data', srcSet, lazy: loading === 'lazy' };
			}
		}
	};

	const [preview, setPreview] = useState(previewInitializer);

	//----------------------------------------------------------------
	// FILE STATE
	//----------------------------------------------------------------

	const { fileState } = useFileState(identifier.id, {
		initialFileState,
		skipRemote,
		collectionName: identifier.collectionName,
		occurrenceKey: identifier.occurrenceKey,
	});

	// Derived from File State
	const isBackendPreviewReady = !!fileState && isImageRepresentationReady(fileState);
	const fileStateErrorMessage = fileState?.status === 'error' ? fileState.message : undefined;

	const {
		preview: localBinary = undefined,
		mediaType = undefined,
		mimeType = undefined,
	} = fileState && fileState?.status !== 'error' ? fileState : {};

	//----------------------------------------------------------------
	// Update status
	//----------------------------------------------------------------

	// TOOD: make a full hook reset (remount) on New identifier or client
	useEffect(() => {
		setStatus('loading');
	}, [identifier]);

	useEffect(() => {
		if (status !== 'error') {
			if (preview || (fileState?.status === 'processed' && !isBackendPreviewReady)) {
				setStatus('complete');
			} else if (!preview && fileState?.status === 'failed-processing' && !isBackendPreviewReady) {
				setStatus('error');
				setError(new MediaFilePreviewError('failed-processing'));
			} else if (!preview && fileState?.status === 'error' && upfrontPreviewStatus === 'resolved') {
				setStatus('error');
				setError(new MediaFilePreviewError('metadata-fetch', toCommonMediaClientError(fileState)));
			} else {
				setStatus('loading');
			}
		}
	}, [
		preview,
		status,
		fileState,
		isBackendPreviewReady,
		fileStateErrorMessage,
		upfrontPreviewStatus,
	]);

	//----------------------------------------------------------------
	// SSR Loading
	//----------------------------------------------------------------

	useEffect(() => {
		if (!fg('media-perf-uplift-mutation-fix')) {
			return;
		}

		const loadPromise = getSSRData(identifier, resizeMode)?.loadPromise;
		if (preview && isSSRDataPreview(preview) && loadPromise) {
			loadPromise
				.then(() => {
					setStatus('complete');
				})
				.catch(() => {
					setPreview(undefined);
				});
		}
	}, [preview, identifier, resizeMode]);

	//----------------------------------------------------------------
	// Preview Fetch Helper
	//----------------------------------------------------------------
	const getAndCacheRemotePreviewRef = useCurrentValueRef(() => {
		return getAndCacheRemotePreview(
			mediaClient,
			identifier.id,
			requestDimensions || {},
			imageURLParams,
			mediaBlobUrlAttrs,
			traceContext,
		);
	});

	//----------------------------------------------------------------
	// Upfront Preview
	//----------------------------------------------------------------
	useEffect(() => {
		// Only fetch upfront (no file state) if there is no preview in the state already
		if (preview) {
			setUpfrontPreviewStatus('resolved');
		} else if (!preview && upfrontPreviewStatus === 'not-resolved' && !skipRemote) {
			// We block any possible future call to this method regardless of the outcome (success or fail)
			// If it fails, the normal preview fetch should occur after the file state is fetched anyways
			setUpfrontPreviewStatus('resolving');
			setIsLoading(true);
			const fetchedDimensions = { ...requestDimensions };
			getAndCacheRemotePreviewRef
				.current()
				.then((newPreview) => {
					// If there are new and bigger dimensions in the props, and the upfront preview is still resolving,
					// the fetched preview is no longer valid, and thus, we dismiss it
					if (!isBigger(fetchedDimensions, requestDimensionsRef.current)) {
						setPreview(newPreview);
					}
				})
				.catch(() => {
					// NO need to log error. If this call fails, a refetch will happen after
				})
				.finally(() => {
					setIsLoading(false);
					setUpfrontPreviewStatus('resolved');
				});
		}
	}, [
		getAndCacheRemotePreviewRef,
		preview,
		requestDimensions,
		requestDimensionsRef,
		skipRemote,
		upfrontPreviewStatus,
	]);

	//----------------------------------------------------------------
	// Cache, Local & Remote Preview
	//----------------------------------------------------------------

	const mediaBlobUrlAttrsRef = useCurrentValueRef(mediaBlobUrlAttrs);
	useEffect(() => {
		const cachedPreview = mediaFilePreviewCache.get(identifier.id, resizeMode);

		// Cached Preview ----------------------------------------------------------------
		if (!preview && cachedPreview && !isBigger(cachedPreview?.dimensions, requestDimensions)) {
			setPreview(cachedPreview);
		}
		// Local Preview ----------------------------------------------------------------
		else if (
			!preview &&
			!isBannedLocalPreview &&
			localBinary &&
			isSupportedLocalPreview(mediaType) &&
			isMimeTypeSupportedByBrowser(mimeType || '')
		) {
			// Local preview is available only if it's supported by browser and supported by Media Card (isSupportedLocalPreview)
			// For example, SVGs are mime type NOT supported by browser but media type supported by Media Card (image)
			// Then, local Preview NOT available
			setIsLoading(true);
			getAndCacheLocalPreview(
				mediaClient,
				identifier.id,
				localBinary,
				requestDimensions || {},
				resizeMode,
				mediaBlobUrlAttrsRef.current,
				identifier.collectionName,
			)
				.then(setPreview)
				.catch((e) => {
					setIsBannedLocalPreview(true);
					// CXP-2723 TODO: We might have to wrap this error in MediaCardError
					setNonCriticalError(e);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
		// Remote Preview ----------------------------------------------------------------
		else if (
			!error &&
			!nonCriticalError &&
			(!preview ||
				isBigger(preview.dimensions, requestDimensions) ||
				// We always refetch SSR preview to be able to browser-cache a version without the token in the query parameters
				isSSRPreview(preview)) &&
			!skipRemote &&
			upfrontPreviewStatus === 'resolved' &&
			isBackendPreviewReady
		) {
			// If the preview is SSR, we can skip the refetch as it will be handled by the global scope promise
			// If the preview is not wider than the requested dimensions, we can skip the refetch
			if (
				preview &&
				isSSRPreview(preview) &&
				!isWider(preview.dimensions, dimensions) &&
				fg('media-perf-uplift-mutation-fix')
			) {
				return;
			}
			setIsLoading(true);
			getAndCacheRemotePreviewRef
				.current()
				.then(setPreview)
				.catch((e) => {
					const wrappedError = ensureMediaFilePreviewError('preview-fetch', e as Error);
					if (!preview) {
						setStatus('error');
						setError(wrappedError);
					} else {
						// If there is already a preview, we consider it a non-critical error
						setNonCriticalError(wrappedError);
					}
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [
		error,
		nonCriticalError,
		getAndCacheRemotePreviewRef,
		identifier.id,
		identifier.collectionName,
		resizeMode,
		isBannedLocalPreview,
		mediaBlobUrlAttrsRef,
		mediaClient,
		preview,
		requestDimensions,
		skipRemote,
		isBackendPreviewReady,
		localBinary,
		mediaType,
		mimeType,
		upfrontPreviewStatus,
		dimensions,
	]);

	//----------------------------------------------------------------
	// Cache ref tracking — prevent blob URL eviction while mounted
	//----------------------------------------------------------------
	useEffect(() => {
		mediaFilePreviewCache.acquire(identifier.id, resizeMode);
		return () => {
			mediaFilePreviewCache.release(identifier.id, resizeMode);
		};
	}, [identifier.id, resizeMode]);

	//----------------------------------------------------------------
	// RETURN
	//----------------------------------------------------------------

	const onImageError = useCallback(
		(failedPreview?: MediaFilePreview): void => {
			if (!failedPreview) {
				return;
			}
			if (isSSRClientPreview(failedPreview)) {
				ssrReliabilityRef.current = {
					...ssrReliabilityRef.current,
					client: createFailedSSRObject(failedPreview, traceContext),
				};
			}

			const isSSR = isSSRDataPreview(failedPreview) || isSSRClientPreview(failedPreview);

			// If the preview failed and it comes from server (global scope / ssrData), it means that we have reused it in client and the error counts for both: server & client.
			if (isSSR) {
				ssrReliabilityRef.current = {
					server: createFailedSSRObject(failedPreview, traceContext),
					client: createFailedSSRObject(failedPreview, traceContext),
				};
			}

			// If the dataURI has been replaced, we can dismiss this error
			if (failedPreview.dataURI !== preview?.dataURI) {
				return;
			}
			const isLocal = isLocalPreview(failedPreview);
			const isRemote = isRemotePreview(failedPreview);
			if (isLocal || isRemote || isSSR) {
				const error = new ImageLoadError(failedPreview?.source);
				mediaFilePreviewCache.remove(identifier.id, resizeMode);

				if (isLocal) {
					setIsBannedLocalPreview(true);
				}
				if (isRemote) {
					setStatus('error');
					setError(error);
				}

				setPreview(undefined);
			}
		},
		[identifier.id, preview?.dataURI, resizeMode, traceContext],
	);

	const onImageLoad = useCallback(
		(newPreview?: MediaFilePreview): void => {
			if (newPreview) {
				if (
					isSSRClientPreview(newPreview) &&
					ssrReliabilityRef.current.client.status === 'unknown'
				) {
					ssrReliabilityRef.current = {
						...ssrReliabilityRef.current,
						client: { status: 'success' },
					};
				}

				/*
        If the image loads successfully and it comes from server (global scope / ssrData), it means that we have reused it in client and the success counts for both: server & client.
      */

				if (isSSRDataPreview(newPreview) && ssrReliabilityRef.current.server.status === 'unknown') {
					ssrReliabilityRef.current = {
						server: { status: 'success' },
						client: { status: 'success' },
					};
				}

				/*
				Handle 'ssr-server' source which is used when:
				1. ssr='server' is passed to getSSRPreview (server-side rendering)
				2. DEFAULT_SSR_PREVIEW is used (placeholder during SSR)
				This ensures ssrReliability is updated to 'success' when the server-rendered image loads.
			*/
				if (
					newPreview.source === 'ssr-server' &&
					ssrReliabilityRef.current.server.status === 'unknown'
				) {
					ssrReliabilityRef.current = {
						server: { status: 'success' },
						client: { status: 'success' },
					};
				}
			}

			// If the dataURI has been replaced, we can dismiss this callback
			if (newPreview?.dataURI !== preview?.dataURI) {
				return;
			}
		},
		[preview?.dataURI],
	);

	// FOR SSR
	const getSsrScriptProps =
		ssr === 'server'
			? (): ScriptHTMLAttributes<HTMLScriptElement> =>
					generateScriptProps(
						identifier,
						preview?.dataURI,
						resizeMode,
						preview?.srcSet,
						requestDimensions,
						ssrReliabilityRef.current.server?.status === 'fail'
							? ssrReliabilityRef.current.server
							: undefined,
						{
							'media-perf-uplift-mutation-fix': fg('media-perf-uplift-mutation-fix'),
							'media-perf-lazy-loading-optimisation': fg('media-perf-lazy-loading-optimisation'),
						},
					)
			: undefined;

	const { copyNodeRef } = useCopyIntent(identifier.id, {
		collectionName: identifier.collectionName,
	});

	// CXP-2723 TODO: should consider simplifying our analytics, and how
	// we might get rid of ssrReliabiltyRef from our hook
	return {
		preview:
			ssr === 'server' && useSrcSet && fg('media-perf-uplift-mutation-fix')
				? DEFAULT_SSR_PREVIEW
				: preview,
		status,
		error,
		nonCriticalError,
		ssrReliability: ssrReliabilityRef.current,
		onImageError,
		onImageLoad,
		getSsrScriptProps,
		copyNodeRef,
	};
};
